import { h, Fragment } from 'preact'
import { useSignal } from '@preact/signals'
import { useRef } from 'preact/hooks'
import { DirectUpload } from '@rails/activestorage'
import { rails_direct_uploads_path } from '../../../routes'
import { MemberPicker } from './MemberPicker.jsx'
import { ModalShell } from './ModalShell.jsx'
import { chatApi } from '../chat-api.js'
import { t } from '../chat-i18n.js'
import { RequiredAsterisk } from '../../../components/shared/RequiredAsterisk.jsx'

/**
 * NewGroupModal — name + members + optional avatar (DirectUpload).
 * Creates room then optionally PATCH avatar_signed_id.
 *
 * Props: { open, onClose, onCreated(room) }
 */
export function NewGroupModal({ open, onClose, onCreated }) {
  const name           = useSignal('')
  const members        = useSignal([])
  const avatarPreview  = useSignal(null)  // object URL or null
  const avatarSignedId = useSignal(null)
  const uploading      = useSignal(false)
  const saving         = useSignal(false)
  const error          = useSignal(null)
  const fileInputRef   = useRef(null)

  function reset() {
    name.value = ''
    members.value = []
    if (avatarPreview.value) URL.revokeObjectURL(avatarPreview.value)
    avatarPreview.value = null
    avatarSignedId.value = null
    uploading.value = false
    saving.value = false
    error.value = null
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleClose() {
    reset()
    onClose?.()
  }

  function onAvatarChange(e) {
    const file = e.target.files?.[0]
    if (!file) return

    if (avatarPreview.value) URL.revokeObjectURL(avatarPreview.value)
    avatarPreview.value = URL.createObjectURL(file)
    avatarSignedId.value = null
    uploading.value = true

    const upload = new DirectUpload(file, rails_direct_uploads_path())
    upload.create((err, blob) => {
      uploading.value = false
      if (err) {
        error.value = String(err)
        return
      }
      avatarSignedId.value = blob.signed_id
    })
  }

  async function handleSubmit(e) {
    e?.preventDefault()
    const groupName = name.value.trim()
    if (!groupName || saving.value || uploading.value) return

    saving.value = true
    error.value = null
    try {
      const createRes = await chatApi.rooms.create({
        name: groupName,
        member_person_ids: members.value.map(p => p.id),
      })
      if (!createRes.ok) {
        const body = await createRes.json.catch(() => ({}))
        error.value = body.error || t('chat.rooms.not_authorized')
        return
      }
      let room = await createRes.json

      if (avatarSignedId.value) {
        const updateRes = await chatApi.rooms.update(room.id, {
          avatar_signed_id: avatarSignedId.value,
        })
        if (updateRes.ok) room = await updateRes.json
      }

      reset()
      onCreated?.(room)
      onClose?.()
    } catch {
      error.value = t('chat.rooms.not_authorized')
    } finally {
      saving.value = false
    }
  }

  const canSubmit = name.value.trim().length > 0 && !saving.value && !uploading.value

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title={t('chat.rooms.new_group_title')}
      iconClass="bi-people"
      footer={
        <>
          <button type="button" class="btn btn-ghost-secondary" onClick={handleClose} disabled={saving.value}>
            {t('chat.rooms.cancel')}
          </button>
          <button type="button" class="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
            {saving.value
              ? <span class="spinner-border spinner-border-sm" role="status" />
              : t('chat.rooms.save')}
          </button>
        </>
      }
    >
      {error.value && <div class="alert alert-danger py-2 small">{error.value}</div>}

      <div class="mb-4 d-flex flex-column align-items-center gap-2">
        <div class="chat-group-avatar-preview">
          {avatarPreview.value
            ? <img src={avatarPreview.value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
            : <i class="bi bi-people-fill" />}
        </div>
        <label class="btn btn-sm btn-outline-secondary" style={{ cursor: 'pointer' }}>
          <i class="bi bi-camera me-1" />
          {t('chat.rooms.change_avatar')}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            class="d-none"
            onChange={onAvatarChange}
            disabled={saving.value}
          />
        </label>
        {uploading.value && (
          <span class="small text-muted">
            <span class="spinner-border spinner-border-sm me-1" role="status" />
          </span>
        )}
      </div>

      <div class="mb-3">
        <label class="form-label fw-medium">
          {t('chat.rooms.group_name_label')}
          <RequiredAsterisk />
        </label>
        <input
          type="text"
          class="form-control"
          placeholder={t('chat.rooms.group_name_placeholder')}
          value={name.value}
          onInput={e => { name.value = e.target.value }}
          autofocus
          required
          disabled={saving.value}
        />
      </div>

      <div class="mb-1">
        <label class="form-label fw-medium">{t('chat.rooms.members_label')}</label>
        <MemberPicker
          selected={members.value}
          onChange={persons => { members.value = persons }}
          multiple
          disabled={saving.value}
        />
      </div>
    </ModalShell>
  )
}
